import { useEffect, useRef, useState } from "react";
import { ArrowLeftRight, Check, Clock, Download, FileSpreadsheet, KeyRound, LineChart, Link2, MessageCircle, MinusCircle, Network, Pencil, Plus, PlusCircle, Search, ShieldCheck, Trash2, Users, UserX, X, XCircle } from "lucide-react";
import { AgentSearchSelect } from "../components/AgentSearchSelect";
import { PinjamSaldoBanner } from "../components/PinjamSaldoBanner";
import { HutangAgenSummary } from "../components/HutangAgenSummary";
import { SearchPicker } from "../components/SearchPicker";
import { InactiveAgentsDialog } from "../components/InactiveAgentsDialog";
import { ChatPanel } from "../components/ChatPanel";
import { VoucherChatSetup } from "../components/VoucherChatSetup";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { FibiLoading } from "../components/FibiLoading";
import { DatePicker } from "../components/DatePicker";

const AGENT_TYPES = ["Teknisi Kepala", "Tuan Rumah", "Teknisi", "Reseller", "Mitra Fasnet", "Reseller Voucher"];
const TYPE_STYLE = {
  "Teknisi Kepala": "bg-amber-500/10 text-amber-400 border-amber-500/20",
  "Tuan Rumah": "bg-sky-500/10 text-sky-400 border-sky-500/20",
  "Teknisi": "bg-slate-500/10 text-slate-400 border-slate-500/20",
  "Reseller": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  "Mitra Fasnet": "bg-orange-500/10 text-orange-400 border-orange-500/20",
  "Reseller Voucher": "bg-teal-500/10 text-teal-400 border-teal-500/20",
};
const SERI_BY_TYPE = { "Teknisi Kepala": "A", "Mitra Fasnet": "A", "Tuan Rumah": "B", "Teknisi": "B", "Reseller": "C", "Reseller Voucher": "C" };
// Normalisasi spasi tak biasa (spasi ganda / non-breaking space dari impor Excel) agar pencarian tetap cocok
const normSpace = (s) => (s || "").replace(/[\s\u00A0\u200B]+/g, " ").trim().toLowerCase();
const BATASI_MENU = [
  { path: "/", label: "Dashboard" },
  { path: "/agen", label: "Agen & Komisi" },
  { path: "/isi-saldo", label: "Isi Saldo" },
  { path: "/hutang-saldo", label: "Hutang Saldo" },
  { path: "/pesan-alat", label: "Order Alat" },
  { path: "/vouchers", label: "Voucher" },
  { path: "/ppp", label: "WiFi Rumahan" },
  { path: "/reset-voucher", label: "Reset Voucher" },
  { path: "/komplain-wifi", label: "Chat Admin" },
  { path: "/laporan/omset", label: "Laporan Omset" },
  { path: "/laporan/ppp", label: "Transaksi PPPoE" },
  { path: "/laporan/wifi", label: "Lap. Omset Voucher" },
  { path: "/laporan/pendapatan-kotor", label: "Pendapatan Kotor" },
  { path: "/pengaturan", label: "Pengaturan" },
  { path: "/lacak-aset", label: "Lacak Aset" },
];
const RESELLER_VOUCHER_HIDDEN = ["/agen", "/pesan-alat", "/hutang-saldo", "/ppp"];

// template bimbingan 1-klik untuk chat dengan akun pending (login Gmail baru)
const GUIDE_TEMPLATES = [
  { label: "Sambutan + panduan isi form", text: "Halo! Selamat datang di Fasnet. Untuk aktivasi akun, silakan isi Username Akun Lama & Nomor WhatsApp Anda di halaman ini, lalu klik Kirim ke Admin." },
  { label: "Penjelasan username lama", text: "Username akun lama = username Anda di sistem Mikhmon/billing lama. Jika lupa, balas pesan ini ya, kami bantu cek." },
  { label: "Sedang diproses", text: "Data Anda sudah kami terima dan sedang kami tautkan ke akun agen. Setelah selesai, login ulang dengan Gmail yang sama lalu masukkan PIN akun Anda." },
  { label: "Akun sudah aktif", text: "Akun Anda sudah aktif! Silakan login ulang dengan Gmail ini, lalu masukkan PIN akun Anda untuk mulai bertransaksi." },
];
const ROLES = [
  { value: "admin", label: "Super Admin" },
  { value: "admin_fasnet", label: "Admin Fasnet" },
  { value: "teknisi_kepala", label: "Teknisi Kepala" },
  { value: "tuan_rumah", label: "Tuan Rumah" },
  { value: "teknisi", label: "Teknisi" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const ROLE_STYLE = {
  admin: "bg-violet-500/10 text-violet-600 border-violet-500/20",
  admin_fasnet: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  teknisi_kepala: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  tuan_rumah: "bg-sky-500/10 text-sky-600 border-sky-500/20",
  teknisi: "bg-slate-500/10 text-slate-500 border-slate-500/20",
};

export default function Agents() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const isSuperAdmin = me?.role === "admin";
  const [agents, setAgents] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [chatTarget, setChatTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", phone: "", email: "", type: "Teknisi", upline_code: "-", user_pin: "", pin: "", hidden_menus: [] });
  const [editAgent, setEditAgent] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", type: "Teknisi", pin: "", hidden_menus: [] });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePin, setDeletePin] = useState("");
  const [requests, setRequests] = useState([]);
  const [approveReq, setApproveReq] = useState(null);
  const [approveForm, setApproveForm] = useState({ name: "", phone: "", email: "", pin: "" });
  const [rejectReq, setRejectReq] = useState(null);
  const [rejectPin, setRejectPin] = useState("");
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ amount: "", note: "", pin: "" });
  const [mutasiTarget, setMutasiTarget] = useState(null);
  const [mutasiLogs, setMutasiLogs] = useState([]);
  const [mutasiDate, setMutasiDate] = useState("");
  const [mutasiTo, setMutasiTo] = useState("");
  const [mutasiFilter, setMutasiFilter] = useState("all");
  const [topupForm, setTopupForm] = useState({ pay_type: "Tunai", amount: "", note: "", pin: "" });
  const [rulesAgent, setRulesAgent] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ to_code: "", amount: "", pin: "" });
  const [transferBusy, setTransferBusy] = useState(false);
  const [routersSearch, setRoutersSearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [pendingUsers, setPendingUsers] = useState([]);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkAgent, setLinkAgent] = useState("");
  const [agentSugOpen, setAgentSugOpen] = useState(false);
  const [agentSugForce, setAgentSugForce] = useState(false);
  const agentSugRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (agentSugRef.current && !agentSugRef.current.contains(e.target)) { setAgentSugOpen(false); setAgentSugForce(false); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const qAgentSug = normSpace(agentSearch);
  const agentSuggestions = qAgentSug
    ? agents.filter((a) => normSpace(`${a.code || ""} ${a.name || ""} ${a.email || ""} ${a.phone || ""}`).includes(qAgentSug)).slice(0, 8)
    : agents.slice(0, 8);
  const showAgentSug = agentSugOpen && (qAgentSug.length >= 3 || agentSugForce);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPin, setImportPin] = useState("123456");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [bulkForm, setBulkForm] = useState({ upline_code: "", type: "percent", value: 20 });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rules, setRules] = useState([]);
  const [savingRules, setSavingRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: "", email: "", role: "teknisi", agent_code: "", user_pin: "", pin: "" });
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deleteUserPin, setDeleteUserPin] = useState("");
  const [showRestricted, setShowRestricted] = useState(false);
  const [routersAgent, setRoutersAgent] = useState(null);
  const [routersList, setRoutersList] = useState([]);
  const [routersChecked, setRoutersChecked] = useState([]);
  const [routersPin, setRoutersPin] = useState("");
  const [downlineSearch, setDownlineSearch] = useState("");

  const [transferTargets, setTransferTargets] = useState([]);
  const load = () => {
    api.get("/agents").then((r) => setAgents(r.data)).catch((e) => toast.error(errMsg(e)));
    if (!isAdmin && me?.agent_code) api.get("/balance/transfer-targets").then((r) => setTransferTargets(r.data)).catch(() => {});
    api.get("/agents/commissions").then((r) => setCommissions(r.data)).catch(() => {});
    api.get("/agents/reseller-requests").then((r) => setRequests(r.data)).catch((e) => toast.error(`Gagal memuat pengajuan reseller: ${errMsg(e)}`));
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (isAdmin) api.get("/users/pending").then((r) => setPendingUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [isAdmin]);

  const verifyPin = (pin) => api.post("/auth/verify-pin", { pin });

  // ---------- Agen ----------
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isAdmin && form.email.trim()) await verifyPin(form.pin);
      const { data } = await api.post("/agents", { ...form, upline_code: form.upline_code === "-" ? "" : form.upline_code });
      if (data.pending) {
        toast.success("Pengajuan reseller terkirim — menunggu persetujuan admin");
      } else if (data.email) {
        toast.success(`Agen ${data.code} ditambahkan — login ${data.email} aktif`);
      } else {
        toast.success(`Agen ${form.code.toUpperCase()} ditambahkan`);
      }
      setOpen(false); setForm({ code: "", name: "", phone: "", email: "", type: "Teknisi", upline_code: "-", user_pin: "", pin: "", hidden_menus: [] }); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const openApprove = (r) => {
    setApproveReq(r);
    setApproveForm({ name: r.name, phone: r.phone || "", email: r.email || "", pin: "" });
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(approveForm.pin);
      const { data } = await api.post(`/agents/reseller-requests/${approveReq.id}/approve`, { name: approveForm.name, phone: approveForm.phone, email: approveForm.email });
      toast.success(`Pengajuan disetujui — reseller ${data.code} dibuat`);
      setApproveReq(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(rejectPin);
      await api.post(`/agents/reseller-requests/${rejectReq.id}/reject`);
      toast.success("Pengajuan reseller ditolak");
      setRejectReq(null); setRejectPin(""); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(editForm.pin);
      await api.put(`/agents/${editAgent.id}`, { name: editForm.name, phone: editForm.phone, type: editForm.type, hidden_menus: editForm.hidden_menus || [], can_open_branch: !!editForm.can_open_branch, upline_code: (editForm.upline_code ?? "").split(" ")[0].trim().toUpperCase() });
      toast.success(`Agen ${editAgent.code} diperbarui`);
      setEditAgent(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(deletePin);
      await api.delete(`/agents/${deleteTarget.id}`);
      toast.success(`Agen ${deleteTarget.code} dihapus`);
      setDeleteTarget(null); setDeletePin(""); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await api.put(`/agents/${rulesAgent.id}/rules`, { rules: rules.filter((r) => r.agent_code) });
      toast.success(`Aturan komisi ${rulesAgent.code} disimpan`);
      setRulesAgent(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSavingRules(false); }
  };

  // ---------- Pengguna ----------
  const submitEditUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(editUserForm.pin);
      await api.put(`/users/${editUser.id}`, { name: editUserForm.name, email: editUserForm.email, role: editUserForm.role, agent_code: editUserForm.agent_code === "-" ? "" : editUserForm.agent_code, pin: editUserForm.user_pin });
      toast.success(`Pengguna ${editUser.email} diperbarui`);
      setEditUser(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitDeleteUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(deleteUserPin);
      await api.delete(`/users/${deleteUserTarget.id}`);
      toast.success(`Pengguna ${deleteUserTarget.email} dihapus`);
      setDeleteUserTarget(null); setDeleteUserPin(""); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const openRouters = (a) => {
    const u = userByCode[a.code];
    setRoutersAgent({ agent: a, user: u });
    setRoutersList([]);
    setRoutersPin("");
    setRoutersSearch("");
    api.get("/routers").then((r) => {
      setRoutersList(r.data);
      setRoutersChecked(r.data.filter((x) => (x.assigned_emails || []).includes((u.email || "").toLowerCase())).map((x) => x.id));
    }).catch((e) => toast.error(errMsg(e)));
  };

  const saveRouters = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(routersPin);
      const { data } = await api.put(`/agents/${routersAgent.agent.code}/routers`, { email: routersAgent.user.email, router_ids: routersChecked });
      toast.success(`Tautan cabang ${routersAgent.agent.code} diperbarui (${data.changed} perubahan)`);
      setRoutersAgent(null);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const commMap = Object.fromEntries(commissions.map((c) => [c.agent_code, c]));
  // Transfer saldo: hanya agen A ke jaringan downline-nya (subtree penuh)
  const mySubtree = transferTargets;
  const transferToUpline = !!transferTargets[0]?.is_upline;
  const submitTransfer = async (e) => {
    e.preventDefault();
    setTransferBusy(true);
    try {
      const { data } = await api.post("/balance/transfer", { to_code: transferForm.to_code, amount: Number(transferForm.amount), pin: transferForm.pin });
      toast.success(`✅ Saldo ${rupiah(data.amount)} sukses terkirim ke agen ${data.to} — ${data.to_name || ""}`, { description: `Saldo Anda kini ${rupiah(data.balance)}`, duration: 8000 });
      setTransferOpen(false);
      setTransferForm({ to_code: "", amount: "", pin: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setTransferBusy(false); }
  };
  // Aturan upline yang TERSIMPAN saat ini (dibaca dari downline) — agar form massal menampilkan nilai asli, bukan default
  const bulkCurrentRule = (() => {
    if (!bulkForm.upline_code) return null;
    const dl = agents.find((a) => a.upline_code === bulkForm.upline_code && (a.commission_rules || []).some((r) => r.agent_code === bulkForm.upline_code));
    return dl ? (dl.commission_rules || []).find((r) => r.agent_code === bulkForm.upline_code) : null;
  })();
  const saveBulkRules = async () => {
    setBulkBusy(true);
    try {
      const val = Number(bulkForm.value) || 0;
      const r = await api.post("/agents/rules/bulk-upline", { ...bulkForm, value: val });
      toast.success(val > 0
        ? `Tersimpan: komisi ${val}${bulkForm.type === "percent" ? "% omset" : " flat/transaksi"} aktif untuk ${r.data.updated} downline ${bulkForm.upline_code}`
        : `Tersimpan: aturan komisi upline DIHAPUS dari ${r.data.updated} downline ${bulkForm.upline_code}`);
      setBulkOpen(false);
      setBulkForm({ upline_code: "", type: "percent", value: 20 });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBulkBusy(false); }
  };

  const submitImport = async () => {
    if (!importFile) return;
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("default_pin", importPin);
      const r = await api.post("/agents/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setImportBusy(false); }
  };

  const downlines = rulesAgent ? agents.filter((a) => a.upline_code === rulesAgent.code) : [];
  const userByCode = Object.fromEntries(users.map((u) => [u.agent_code, u]).filter(([c]) => c));
  const orphanUsers = users.filter((u) => !u.agent_code || !agents.some((a) => a.code === u.agent_code));
  const openEditUser = (u) => { setEditUser(u); setEditUserForm({ name: u.name, email: u.email || "", role: u.role, agent_code: u.agent_code || "", user_pin: "", pin: "" }); };
  const openDeleteUser = (u) => { setDeleteUserTarget(u); setDeleteUserPin(""); };

  const openAdjust = (a, mode) => {
    setAdjustTarget({ agent: a, user: userByCode[a.code], mode });
    setAdjustForm({ amount: "", note: "", pin: "" });
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(adjustForm.pin);
      const amt = Number(adjustForm.amount);
      const cur = adjustTarget.user.balance || 0;
      const payload = adjustTarget.mode === "sub"
        ? { amount: amt, mode: "sub", note: adjustForm.note || "Potong saldo manual (Daftar Agen)" }
        : { amount: cur + amt, mode: "set", note: adjustForm.note || "Tambah saldo manual (Daftar Agen)" };
      const { data } = await api.put(`/users/${adjustTarget.user.id}/balance`, payload);
      toast.success(`Saldo ${adjustTarget.agent.name} kini ${rupiah(data.balance)}`);
      setAdjustTarget(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const loadMutasi = (u, from, to = mutasiTo) => {
    setMutasiDate(from || "");
    setMutasiTo(to || "");
    const params = { user_id: u.id };
    if (from && to) { params.date_from = from; params.date_to = to; } else if (from) { params.date = from; }
    api.get("/balance/logs", { params })
      .then((r) => setMutasiLogs(r.data))
      .catch(() => setMutasiLogs([]));
  };

  const openMutasi = (a) => {
    const u = userByCode[a.code];
    setMutasiTarget({ agent: a, user: u });
    setMutasiLogs([]);
    setTopupForm({ pay_type: "Tunai", amount: "", note: "", pin: "" });
    loadMutasi(u, "");
  };

  const submitTopup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyPin(topupForm.pin);
      const amt = Number(topupForm.amount);
      const cur = mutasiTarget.user.balance || 0;
      if (topupForm.pay_type === "Hutang Saldo") {
        await api.post("/debts", { email: mutasiTarget.user.email, amount: amt, note: topupForm.note || `bon per ${new Date().toLocaleDateString("id-ID")}` });
        toast.success(`Saldo ${mutasiTarget.agent.name} +${rupiah(amt)} sebagai HUTANG`);
        setTopupForm({ pay_type: "Tunai", amount: "", note: "", pin: "" });
        load();
        loadMutasi({ ...mutasiTarget.user, balance: cur + amt }, mutasiDate);
        setMutasiTarget((t) => ({ ...t, user: { ...t.user, balance: cur + amt }, debtKey: (t.debtKey || 0) + 1 }));
        return;
      }
      const note = `[${topupForm.pay_type}] ${topupForm.note || "isi saldo"} oleh ${me?.name || "admin"}`;
      const { data } = await api.put(`/users/${mutasiTarget.user.id}/balance`, { amount: cur + amt, mode: "set", note });
      toast.success(`Saldo ${mutasiTarget.agent.name} kini ${rupiah(data.balance)}`);
      setTopupForm({ pay_type: "Tunai", amount: "", note: "", pin: "" });
      load();
      loadMutasi({ ...mutasiTarget.user, balance: data.balance }, mutasiDate);
      setMutasiTarget((t) => ({ ...t, user: { ...t.user, balance: data.balance } }));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const [filterUpline, setFilterUpline] = useState("all");
  const uplineOptions = [...new Set(agents.map((a) => a.upline_code).filter(Boolean))].sort();
  const uplineName = Object.fromEntries(agents.map((a) => [a.code, a.name]));
  const downlineCount = agents.reduce((acc, a) => { if (a.upline_code) acc[a.upline_code] = (acc[a.upline_code] || 0) + 1; return acc; }, {});
  const doLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${linkTarget.id}/link-pending`, { target_uid: linkAgent });
      toast.success(`Email ${data.email} berhasil tertaut ke akun agen`);
      setLinkTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const [rejectId, setRejectId] = useState(null);
  const doReject = async (p) => {
    setBusy(true);
    try {
      await api.delete(`/users/pending/${p.id}`);
      toast.success(`Pengajuan aktivasi ${p.email} ditolak`);
      setRejectId(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const [netDetail, setNetDetail] = useState(null);
  const [netDetailBusy, setNetDetailBusy] = useState(false);
  const openNetDetail = async (tk, codes, mode, dateFrom = "", dateTo = "") => {
    const base = { tk, codes, code: tk.code, name: tk.name, mode, dateFrom, dateTo };
    setNetDetail({ ...base, rows: [] });
    setNetDetailBusy(true);
    try {
      const qs = new URLSearchParams({ codes: codes.join(","), mode });
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const { data } = await api.get(`/agents/network-detail?${qs}`);
      setNetDetail({ ...base, rows: data.rows || [] });
    } catch (err) { toast.error(errMsg(err)); setNetDetail(null); } finally { setNetDetailBusy(false); }
  };

  const exportNetCsv = () => {
    if (!netDetail?.rows?.length) return;
    const isKom = netDetail.mode === "komisi";
    const head = isKom ? ["Tanggal", "Tipe", "Item", "Qty", "Dari Agen (Pembuat)", "Penerima Komisi", "Komisi"] : ["Tanggal", "Tipe", "Item", "Qty", "Oleh", "Total"];
    const lines = netDetail.rows.map((r) => isKom
      ? [fmtDateTime(r.created_at), (r.type || "").toUpperCase(), r.item_name, r.qty, `${r.sold_by || "-"} ${r.sold_by_name || ""}`.trim(), `${r.agent_code} ${r.agent_name}`.trim(), r.amount]
      : [fmtDateTime(r.created_at), (r.type || "").toUpperCase(), r.item_name, r.qty, `${r.sold_by || "-"} ${r.sold_by_name || ""}`.trim(), r.total]);
    const csv = "\ufeff" + [head, ...lines].map((cols) => cols.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = `${isKom ? "komisi" : "transaksi"}-${netDetail.code}-${netDetail.dateFrom || "awal"}_${netDetail.dateTo || "kini"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportNetPdf = async () => {
    if (!netDetail?.rows?.length) return;
    try {
      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const isKom = netDetail.mode === "komisi";
      const doc = new jsPDF();
      doc.setFontSize(13);
      doc.text(`${isKom ? "Detail Komisi Jaringan" : "Detail Transaksi"} — ${netDetail.code} ${netDetail.name}`, 14, 14);
      doc.setFontSize(9);
      doc.text(`Periode: ${netDetail.dateFrom || "awal"} s/d ${netDetail.dateTo || "sekarang"} • ${netDetail.rows.length} baris`, 14, 20);
      const total = netDetail.rows.reduce((a, r) => a + (isKom ? r.amount : r.total), 0);
      autoTable(doc, {
        startY: 24,
        head: [isKom ? ["Tanggal", "Tipe", "Item", "Qty", "Dari (Pembuat)", "Penerima", "Komisi"] : ["Tanggal", "Tipe", "Item", "Qty", "Oleh", "Total"]],
        body: netDetail.rows.map((r) => isKom
          ? [fmtDateTime(r.created_at), (r.type || "").toUpperCase(), r.item_name, r.qty, `${r.sold_by || "-"} ${r.sold_by_name || ""}`.trim(), `${r.agent_code} ${r.agent_name}`.trim(), rupiah(r.amount)]
          : [fmtDateTime(r.created_at), (r.type || "").toUpperCase(), r.item_name, r.qty, `${r.sold_by || "-"} ${r.sold_by_name || ""}`.trim(), rupiah(r.total)]),
        foot: [isKom ? ["", "", "", "", "", "Total Komisi", rupiah(total)] : ["", "", "", "", "Omset", rupiah(total)]],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [240, 78, 35] },
      });
      doc.save(`${isKom ? "komisi" : "transaksi"}-${netDetail.code}-${netDetail.dateFrom || "awal"}_${netDetail.dateTo || "kini"}.pdf`);
    } catch { toast.error("Gagal membuat PDF"); }
  };

  const [recapOpen, setRecapOpen] = useState(false);
  const [recapData, setRecapData] = useState(null);
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapFrom, setRecapFrom] = useState("");
  const [recapTo, setRecapTo] = useState("");
  const loadRecap = async (df = "", dt = "") => {
    setRecapBusy(true);
    try {
      const qs = new URLSearchParams();
      if (df) qs.set("date_from", df);
      if (dt) qs.set("date_to", dt);
      const { data } = await api.get(`/agents/network-recap?${qs}`);
      setRecapData(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setRecapBusy(false); }
  };

  const exportRecapCsv = () => {
    if (!recapData?.rows?.length) return;
    const head = ["Kode TK", "Nama Teknisi Kepala", "Downline", "Transaksi", "Komisi Jaringan"];
    const lines = recapData.rows.map((r) => [r.tk_code, r.tk_name, r.downlines, r.trx, r.komisi]);
    lines.push(["TOTAL", "", "", recapData.total_trx, recapData.total_komisi]);
    const csv = "\ufeff" + [head, ...lines].map((cols) => cols.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = `rekap-komisi-jaringan-${recapFrom || "awal"}_${recapTo || "kini"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportRecapPdf = async () => {
    if (!recapData?.rows?.length) return;
    try {
      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const doc = new jsPDF();
      doc.setFontSize(13);
      doc.text("Rekap Komisi Jaringan per Teknisi Kepala", 14, 14);
      doc.setFontSize(9);
      doc.text(`Periode: ${recapFrom || "awal"} s/d ${recapTo || "sekarang"}`, 14, 20);
      autoTable(doc, {
        startY: 24,
        head: [["Kode TK", "Nama Teknisi Kepala", "Downline", "Transaksi", "Komisi Jaringan"]],
        body: recapData.rows.map((r) => [r.tk_code, r.tk_name, r.downlines, r.trx, rupiah(r.komisi)]),
        foot: [["TOTAL", "", "", recapData.total_trx, rupiah(recapData.total_komisi)]],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [240, 78, 35] },
      });
      doc.save(`rekap-komisi-jaringan-${recapFrom || "awal"}_${recapTo || "kini"}.pdf`);
    } catch { toast.error("Gagal membuat PDF"); }
  };

  const visibleAgents = agents
    .filter((a) => !showRestricted || (a.hidden_menus || []).length > 0)
    .filter((a) => filterUpline === "all" || (filterUpline === "none" ? !a.upline_code : a.upline_code === filterUpline))
    .filter((a) => !agentSearch || normSpace(`${a.code} ${a.name} ${a.email} ${a.phone}`).includes(normSpace(agentSearch)));

  const mutasiIn = mutasiLogs.reduce((a, l) => a + Math.max(0, l.amount), 0);
  const mutasiOut = mutasiLogs.reduce((a, l) => a + Math.min(0, l.amount), 0);
  const mutasiKomisi = mutasiLogs.filter((l) => /komisi|insentive|incentive/i.test(l.note || "")).reduce((a, l) => a + l.amount, 0);

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Agen & Komisi</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola jaringan agen, aturan komisi lintas agen, dan pendaftaran email pengguna (Teknisi Kepala / Tuan Rumah / Teknisi / Mitra Fasnet)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant={showRestricted ? "default" : "outline"} onClick={() => setShowRestricted(!showRestricted)} data-testid="btn-filter-restricted">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Hanya yang dibatasi{showRestricted ? ` (${visibleAgents.length})` : ""}
          </Button>
          <SearchPicker value={filterUpline} onChange={setFilterUpline} placeholder="Filter upline" title="Filter Upline" searchPlaceholder="Cari kode / nama upline..." testid="select-filter-upline" triggerClassName="w-full sm:w-56"
            options={[{ value: "all", label: "Semua Upline" }, { value: "none", label: "Tanpa Upline" }, ...uplineOptions.map((c) => ({ value: c, label: `${c} — ${uplineName[c] || ""} (${downlineCount[c] || 0})` }))]} />
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="btn-import-excel">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Impor Excel
            </Button>
          )}
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="btn-bulk-commission">
              <Network className="w-4 h-4 mr-1.5" /> Komisi Upline Massal
            </Button>
          )}
          {isSuperAdmin && (
            <Button variant="outline" className="text-rose-400 border-rose-500/40" onClick={() => setInactiveOpen(true)} data-testid="btn-inactive-agents">
              <UserX className="w-4 h-4 mr-1.5" /> Agen Tidak Aktif
            </Button>
          )}
          {mySubtree.length > 0 && (
            <Button variant="outline" className="text-emerald-400 border-emerald-500/40" onClick={() => { setTransferForm({ to_code: "", amount: "", pin: "" }); setTransferOpen(true); }} data-testid="btn-transfer-saldo">
              <ArrowLeftRight className="w-4 h-4 mr-1.5" /> Transfer Saldo
            </Button>
          )}
          <Button onClick={() => setOpen(true)} data-testid="btn-add-agent"><Plus className="w-4 h-4 mr-1.5" /> {isSuperAdmin ? "Tambah Agen / Member" : isAdmin ? "Tambah Agen" : "Tambah Reseller"}</Button>
        </div>
      </div>

      {isAdmin && pendingUsers.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30" data-testid="pending-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2 text-amber-400"><Clock className="w-4 h-4" /> Menunggu Aktivasi ({pendingUsers.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">Agen yang login Google dengan email belum terdaftar. Tautkan email mereka ke akun agen yang sudah ada — saldo, komisi & hierarki akun lama tetap terbawa.</p>
            {pendingUsers.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/20 bg-background/40 px-4 py-2.5" data-testid={`pending-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.name} <span className="font-mono text-xs text-muted-foreground">{p.email}</span></div>
                  <div className="text-[11px] text-muted-foreground">Username Akun Lama: <b className="text-foreground">{p.old_username || "-"}</b> • WA: <b className="text-foreground">{p.whatsapp || "-"}</b> • {fmtDateTime(p.created_at)}</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" title="Kirim pesan — bimbing agen mendaftar" onClick={() => setChatTarget({ name: p.name, channel: `agent:${p.id}`, guide: true })} data-testid={`btn-chat-pending-${p.id}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> Chat
                  </Button>
                  {rejectId === p.id ? (
                    <>
                      <Button size="sm" variant="destructive" disabled={busy} onClick={() => doReject(p)} data-testid={`btn-reject-confirm-${p.id}`}>Ya, Tolak</Button>
                      <Button size="sm" variant="ghost" onClick={() => setRejectId(null)} data-testid={`btn-reject-cancel-${p.id}`}>Batal</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/30 hover:bg-rose-500/10" onClick={() => setRejectId(p.id)} data-testid={`btn-reject-${p.id}`}>Tolak</Button>
                  )}
                  <Button size="sm" onClick={() => { setLinkTarget(p); setLinkAgent(""); }} data-testid={`btn-link-${p.id}`}><Link2 className="w-3.5 h-3.5 mr-1" /> Tautkan</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!linkTarget} onOpenChange={(o) => { if (!o) setLinkTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-link-pending">
          <DialogHeader><DialogTitle className="text-base font-heading">Tautkan Email ke Akun Agen</DialogTitle></DialogHeader>
          {linkTarget && (
            <div className="space-y-3">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-xs text-amber-500">
                Email Google: <b>{linkTarget.email}</b><br />Username Akun Lama: <b>{linkTarget.old_username || "-"}</b> • WA: <b>{linkTarget.whatsapp || "-"}</b>
              </div>
              <div className="space-y-1.5">
                <Label>Akun agen yang sudah ada (emailnya akan diganti ke email Google ini)</Label>
                <AgentSearchSelect
                  options={users.filter((u) => u.role !== "pending").map((u) => ({ value: u.id, label: `${u.agent_code ? u.agent_code + " — " : ""}${u.name} (${u.email})`, search: `${u.agent_code || ""} ${u.name || ""} ${u.email || ""}` }))}
                  value={linkAgent} onChange={setLinkAgent} placeholder="Pilih akun agen lama" testid="select-link-agent" />
              </div>
              <p className="text-[11px] text-muted-foreground">Setelah ditautkan, agen cukup login ulang dengan Gmail tersebut dan memakai PIN akun lamanya. Akun pending otomatis dihapus.</p>
              <Button className="w-full" disabled={!linkAgent || busy} onClick={doLink} data-testid="btn-confirm-link">{busy ? "Memproses..." : "Tautkan Sekarang"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={recapOpen} onOpenChange={setRecapOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dialog-net-recap">
          <DialogHeader><DialogTitle className="text-base font-heading">Rekap Komisi Jaringan per Teknisi Kepala</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-[10px]">Dari</Label>
                <DatePicker className="h-8 text-xs w-36" value={recapFrom} onChange={(v) => setRecapFrom(v)} testid="recap-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px]">Sampai</Label>
                <DatePicker className="h-8 text-xs w-36" value={recapTo} onChange={(v) => setRecapTo(v)} testid="recap-date-to" />
              </div>
              <Button size="sm" variant="outline" className="h-8" disabled={recapBusy} onClick={() => loadRecap(recapFrom, recapTo)} data-testid="btn-recap-apply">Terapkan</Button>
              <div className="ml-auto flex gap-1.5">
                <Button size="sm" variant="outline" className="h-8" disabled={recapBusy || !recapData?.rows?.length} onClick={exportRecapCsv} data-testid="btn-recap-csv"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" className="h-8" disabled={recapBusy || !recapData?.rows?.length} onClick={exportRecapPdf} data-testid="btn-recap-pdf"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
              </div>
            </div>
            {recapBusy ? (
              <div><FibiLoading text="FIBI sedang memuat rekap..." size={20} /></div>
            ) : !recapData?.rows?.length ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="recap-empty">Belum ada data</p>
            ) : (
              <div className="max-h-[50vh] overflow-y-auto space-y-1.5 pr-1">
                {recapData.rows.map((r) => (
                  <div key={r.tk_code} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 text-xs" data-testid={`recap-row-${r.tk_code}`}>
                    <span className="font-mono font-semibold w-12 shrink-0">{r.tk_code}</span>
                    <span className="min-w-0 flex-1 truncate">{r.tk_name} <span className="text-muted-foreground">• {r.downlines} downline • {r.trx} transaksi</span></span>
                    <span className="font-mono font-semibold text-emerald-400">{rupiah(r.komisi)}</span>
                  </div>
                ))}
              </div>
            )}
            {!recapBusy && recapData?.rows?.length > 0 && (
              <p className="text-[11px] font-mono text-muted-foreground text-right" data-testid="recap-summary">{recapData.rows.length} jaringan • {recapData.total_trx} transaksi • Total komisi {rupiah(recapData.total_komisi)}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!netDetail} onOpenChange={(o) => { if (!o) setNetDetail(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dialog-net-detail">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">{netDetail?.mode === "komisi" ? "Detail Komisi Jaringan" : "Detail Transaksi"} — {netDetail?.code} {netDetail?.name}</DialogTitle>
          </DialogHeader>
          {netDetail && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px]">Dari</Label>
                  <DatePicker className="h-8 text-xs w-36" value={netDetail.dateFrom} onChange={(v) => setNetDetail((d) => (d ? { ...d, dateFrom: v } : d))} testid="net-detail-date-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px]">Sampai</Label>
                  <DatePicker className="h-8 text-xs w-36" value={netDetail.dateTo} onChange={(v) => setNetDetail((d) => (d ? { ...d, dateTo: v } : d))} testid="net-detail-date-to" />
                </div>
                <Button size="sm" variant="outline" className="h-8" disabled={netDetailBusy} onClick={() => openNetDetail(netDetail.tk, netDetail.codes, netDetail.mode, netDetail.dateFrom, netDetail.dateTo)} data-testid="btn-net-detail-apply">Terapkan</Button>
                <div className="ml-auto flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-8" disabled={netDetailBusy || !netDetail.rows.length} onClick={exportNetCsv} data-testid="btn-net-detail-csv"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV</Button>
                  <Button size="sm" variant="outline" className="h-8" disabled={netDetailBusy || !netDetail.rows.length} onClick={exportNetPdf} data-testid="btn-net-detail-pdf"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                </div>
              </div>
              {netDetailBusy ? (
                <div><FibiLoading text="FIBI sedang memuat detail..." size={20} /></div>
              ) : !netDetail.rows.length ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="net-detail-empty">Belum ada data</p>
              ) : (
                <div className="max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">
                  {netDetail.rows.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 text-xs" data-testid={`net-detail-row-${r.id}`}>
                      <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">{fmtDateTime(r.created_at)}</span>
                      <span className="min-w-0 flex-1 truncate"><span className="uppercase text-muted-foreground font-mono text-[9px]">{r.type}</span> {r.item_name}{String(r.item_name).includes(`x${r.qty}`) ? "" : ` x${r.qty}`}{netDetail.mode === "komisi" ? (r.sold_by ? <span className="text-muted-foreground"> — dari <span className="font-mono text-sky-400">{r.sold_by}</span> {r.sold_by_name}{r.agent_code !== r.sold_by ? <span className="text-[10px]"> (penerima {r.agent_code})</span> : null}</span> : null) : r.sold_by ? <span className="text-muted-foreground"> — oleh <span className="font-mono">{r.sold_by}</span> {r.sold_by_name}</span> : null}</span>
                      <span className={`font-mono font-semibold ${netDetail.mode === "komisi" ? "text-emerald-400" : "text-foreground"}`}>{rupiah(netDetail.mode === "komisi" ? r.amount : r.total)}</span>
                    </div>
                  ))}
                </div>
              )}
              {!netDetailBusy && netDetail.rows.length > 0 && (
                <p className="text-[11px] font-mono text-muted-foreground text-right" data-testid="net-detail-summary">
                  {netDetail.rows.length} baris • {netDetail.mode === "komisi" ? `Total komisi ${rupiah(netDetail.rows.reduce((a, r) => a + r.amount, 0))}` : `Omset ${rupiah(netDetail.rows.reduce((a, r) => a + r.total, 0))}`}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Users className="w-4 h-4 text-sky-400" /> Daftar Agen / Member</CardTitle>
          {isAdmin && <HutangAgenSummary refreshKey={mutasiTarget?.debtKey || 0} onOpenMutasi={(a) => { const ag = agents.find((x) => x.code === a.agent_code) || agents.find((x) => (x.email || "").toLowerCase() === a.email.toLowerCase()); if (ag) openMutasi(ag); else toast.error("Data agen tidak ditemukan"); }} />}
          {isAdmin && (
            <Button variant="outline" size="sm" className="sm:ml-auto" onClick={() => { setRecapOpen(true); loadRecap(recapFrom, recapTo); }} data-testid="btn-net-recap"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Rekap Komisi Jaringan</Button>
          )}
          <div className="relative w-full sm:w-72" ref={agentSugRef}>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cari kode / nama / email / HP..." value={agentSearch}
              onChange={(e) => { setAgentSearch(e.target.value); setAgentSugOpen(true); setAgentSugForce(false); }}
              onFocus={() => setAgentSugOpen(true)}
              className="pl-9 pr-16 h-9" data-testid="input-search-agent" autoComplete="off" />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {agentSearch && (
                <button type="button" title="Hapus pencarian" onClick={() => { setAgentSearch(""); setAgentSugOpen(false); setAgentSugForce(false); }}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="btn-agent-search-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button type="button" title="Cari agen" onClick={() => { setAgentSugForce(true); setAgentSugOpen(true); }}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="btn-agent-search">
                <Search className="w-3.5 h-3.5" />
              </button>
            </div>
            {showAgentSug && agentSuggestions.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg max-h-56 overflow-y-auto" data-testid="agent-suggestions">
                {agentSuggestions.map((a) => (
                  <button key={a.code || a.email} type="button" onClick={() => { setAgentSearch(a.name || a.code || a.email); setAgentSugOpen(false); setAgentSugForce(false); }}
                    className="w-full px-3 py-2 text-left hover:bg-accent/60 transition-colors border-b border-border/50 last:border-0"
                    data-testid={`agent-sug-option-${a.code || a.email}`}>
                    <div className="text-xs font-medium truncate">{a.name || a.email}</div>
                    <div className="text-[10px] font-mono text-muted-foreground truncate">{a.code ? `${a.code} • ` : ""}{a.email}</div>
                  </button>
                ))}
              </div>
            )}
            {showAgentSug && !agentSuggestions.length && (
              <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-lg px-3 py-2 text-xs text-muted-foreground" data-testid="agent-sug-empty">Agen tidak ditemukan</div>
            )}
          </div>
        </CardHeader>
        <div className="md:hidden divide-y divide-border" data-testid="agents-cards">
          {!visibleAgents.length && <p className="text-center py-8 text-muted-foreground text-sm" data-testid="agents-empty-mobile">{agentSearch ? `Tidak ada agen yang cocok dengan "${agentSearch}"` : "Belum ada agen."}</p>}
          {visibleAgents.map((a) => (
            <div key={a.id} className="p-3.5 space-y-2" data-testid={`agent-card-${a.code}`}>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-xs font-mono bg-sky-500/10 text-sky-400 border border-sky-500/20">{a.code}</span>
                <span className="font-medium text-sm flex-1 min-w-0 truncate">{a.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${TYPE_STYLE[a.type] || TYPE_STYLE["Teknisi"]}`}>{a.type || "Teknisi"}</span>
              </div>
              <div className="text-[11px] font-mono text-muted-foreground space-y-0.5">
                {userByCode[a.code]?.email && <div className="truncate">{userByCode[a.code].email}</div>}
                <div>{a.phone || "-"}{a.upline_code ? ` • Upline: ${a.upline_code}` : ""}</div>
                {(a.hidden_menus || []).length > 0 && <div className="text-violet-400">{a.hidden_menus.length} menu dibatasi</div>}
              </div>
              <div className="flex items-center gap-3 text-[11px] font-mono flex-wrap">
                <span className="text-muted-foreground">Saldo <span className={`font-semibold ${(userByCode[a.code]?.balance || 0) < 0 ? "text-rose-500" : "text-emerald-500"}`} data-testid={`card-saldo-${a.code}`}>{isAdmin && userByCode[a.code] ? rupiah(userByCode[a.code].balance || 0) : "-"}</span></span>
                {downlineCount[a.code] ? <span className="text-muted-foreground">DL <span className="text-sky-400 font-semibold">{downlineCount[a.code]}</span></span> : null}
              </div>
              {(isAdmin || isSuperAdmin) && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  {isAdmin && userByCode[a.code] && (
                    <>
                      <Button size="sm" className="bg-sky-600 hover:bg-sky-500 px-2 h-8" title="Kurangi saldo manual" onClick={() => openAdjust(a, "sub")} data-testid={`card-saldo-minus-${a.code}`}>-$</Button>
                      <Button size="sm" className="bg-sky-600 hover:bg-sky-500 px-2 h-8" title="Tambah saldo manual" onClick={() => openAdjust(a, "add")} data-testid={`card-saldo-plus-${a.code}`}>+$</Button>
                      <Button size="sm" variant="secondary" className="px-2 h-8" title="Mutasi terakhir" onClick={() => openMutasi(a)} data-testid={`card-mutasi-${a.code}`}><LineChart className="w-3.5 h-3.5" /></Button>
                      <VoucherChatSetup userId={userByCode[a.code].id} agentCode={a.code} testid={`card-${a.code}`} labeled />
                    </>
                  )}
                  {isSuperAdmin && (
                    <>
                      <Button size="icon" variant="outline" className="h-8 w-8" title="Jaringan komisi" onClick={() => { setRulesAgent(a); setRules(a.commission_rules || []); }} data-testid={`card-rules-agent-${a.code}`}><Network className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-8 w-8" title="Edit agen" onClick={() => { setEditAgent(a); setEditForm({ name: a.name, phone: a.phone || "", type: a.type || "Teknisi", pin: "", hidden_menus: a.hidden_menus || [], can_open_branch: !!a.can_open_branch, upline_code: a.upline_code || "" }); }} data-testid={`card-edit-agent-${a.code}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      {userByCode[a.code] && <Button size="icon" variant="outline" className="h-8 w-8" title="Atur cabang tertaut" onClick={() => openRouters(a)} data-testid={`card-routers-agent-${a.code}`}><Link2 className="w-3.5 h-3.5" /></Button>}
                      {userByCode[a.code] && <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-400 border-emerald-500/40" title="Chat dengan agen ini" onClick={() => setChatTarget({ name: a.name, channel: `agent:${userByCode[a.code].id}` })} data-testid={`card-chat-agent-${a.code}`}><MessageCircle className="w-3.5 h-3.5" /></Button>}
                      {userByCode[a.code] && <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-400" title="Edit akses login" onClick={() => openEditUser(userByCode[a.code])} data-testid={`card-edit-user-${a.code}`}><KeyRound className="w-3.5 h-3.5" /></Button>}
                      {userByCode[a.code] && userByCode[a.code].id !== me?.id && <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-400" title="Hapus akses login" onClick={() => openDeleteUser(userByCode[a.code])} data-testid={`card-delete-user-${a.code}`}><UserX className="w-3.5 h-3.5" /></Button>}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" title="Hapus agen" onClick={() => { setDeleteTarget(a); setDeletePin(""); }} data-testid={`card-delete-agent-${a.code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden md:block">
        <Table className="min-w-[1020px]" data-testid="agents-table">
          <TableHeader>
            <TableRow>
              <TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Tipe</TableHead><TableHead>No. HP</TableHead>
              <TableHead>Downline</TableHead><TableHead>Transaksi</TableHead><TableHead>Saldo</TableHead><TableHead>Mutasi Saldo</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!visibleAgents.length && (!(isSuperAdmin && orphanUsers.length) || agentSearch || showRestricted) && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground" data-testid="agents-empty">{showRestricted ? "Tidak ada agen yang sedang dibatasi menunya" : agentSearch ? `Tidak ada agen yang cocok dengan "${agentSearch}"` : "Belum ada agen. Tambahkan agen untuk mulai membagi komisi."}</TableCell></TableRow>}
            {visibleAgents.map((a) => (
              <TableRow key={a.id} data-testid={`agent-row-${a.code}`}>
                <TableCell><span className="px-2 py-0.5 rounded text-xs font-mono bg-sky-500/10 text-sky-400 border border-sky-500/20">{a.code}</span></TableCell>
                <TableCell className="font-medium">{a.name}
                  {a.upline_code && <span className="block text-xs text-muted-foreground font-mono">Upline: {a.upline_code}</span>}
                  {(a.commission_rules || []).length > 0 && (
                    <span className="block text-xs text-muted-foreground font-mono">{(a.commission_rules || []).length} aturan komisi jaringan</span>
                  )}
                  {(a.hidden_menus || []).length > 0 && (
                    <span
                      className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-mono bg-violet-500/10 text-violet-400 border border-violet-500/20"
                      title={`Menu disembunyikan: ${BATASI_MENU.filter((m) => a.hidden_menus.includes(m.path)).map((m) => m.label).join(", ") || a.hidden_menus.join(", ")}`}
                      data-testid={`agent-restricted-${a.code}`}
                    >
                      <ShieldCheck className="w-3 h-3" /> {a.hidden_menus.length} menu dibatasi
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-muted-foreground text-xs" data-testid={`agent-email-${a.code}`}>{userByCode[a.code]?.email || "-"}</TableCell>
                <TableCell>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono border ${TYPE_STYLE[a.type] || TYPE_STYLE["Teknisi"]}`} data-testid={`agent-type-${a.code}`}>
                    {a.type || "Teknisi"}
                  </span>
                  {userByCode[a.code] && (
                    <span className="block text-[10px] text-muted-foreground font-mono mt-0.5" data-testid={`agent-role-${a.code}`}>Role: {ROLE_LABEL[userByCode[a.code].role] || userByCode[a.code].role}</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-muted-foreground">{a.phone || "-"}</TableCell>
                <TableCell className="font-mono" data-testid={`agent-downline-${a.code}`}>{downlineCount[a.code] ? <span className="text-sky-400 font-semibold">{downlineCount[a.code]}</span> : "-"}</TableCell>
                <TableCell className="font-mono">{commMap[a.code]?.transactions || 0}</TableCell>
                <TableCell className={`font-mono font-semibold ${(userByCode[a.code]?.balance || 0) < 0 ? "text-rose-500" : "text-emerald-500"}`} data-testid={`agent-saldo-${a.code}`}>
                  {isAdmin && userByCode[a.code] ? rupiah(userByCode[a.code].balance || 0) : "-"}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {isAdmin && userByCode[a.code] ? (
                    <div className="flex gap-1">
                      <Button size="sm" className="bg-sky-600 hover:bg-sky-500 px-2" title="Kurangi saldo manual" onClick={() => openAdjust(a, "sub")} data-testid={`btn-saldo-minus-${a.code}`}>-$</Button>
                      <Button size="sm" className="bg-sky-600 hover:bg-sky-500 px-2" title="Tambah saldo manual" onClick={() => openAdjust(a, "add")} data-testid={`btn-saldo-plus-${a.code}`}>+$</Button>
                      <Button size="sm" variant="secondary" className="px-2" title="10 mutasi terakhir" onClick={() => openMutasi(a)} data-testid={`btn-mutasi-${a.code}`}><LineChart className="w-3.5 h-3.5" /></Button>
                      <VoucherChatSetup userId={userByCode[a.code].id} agentCode={a.code} testid={`row-${a.code}`} labeled />
                    </div>
                  ) : <span className="text-xs text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {isSuperAdmin ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setRulesAgent(a); setRules(a.commission_rules || []); }} data-testid={`btn-rules-agent-${a.code}`}>
                        <Network className="w-3.5 h-3.5 mr-1" /> Jaringan
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => { setEditAgent(a); setEditForm({ name: a.name, phone: a.phone || "", type: a.type || "Teknisi", pin: "", hidden_menus: a.hidden_menus || [], can_open_branch: !!a.can_open_branch, upline_code: a.upline_code || "" }); }} data-testid={`btn-edit-agent-${a.code}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {userByCode[a.code] && (
                        <Button size="sm" variant="outline" title="Atur cabang tertaut" onClick={() => openRouters(a)} data-testid={`btn-routers-agent-${a.code}`}>
                          <Link2 className="w-3.5 h-3.5 mr-1" /> Cabang
                        </Button>
                      )}
                      {userByCode[a.code] && (
                        <Button size="sm" variant="outline" className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" title="Chat dengan agen ini" onClick={() => setChatTarget({ name: a.name, channel: `agent:${userByCode[a.code].id}` })} data-testid={`btn-chat-agent-${a.code}`}>
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Chat
                        </Button>
                      )}
                      {userByCode[a.code] && (
                        <>
                          <Button size="icon" variant="ghost" className="text-sky-400" title="Edit akses login (nama/role/PIN)" onClick={() => openEditUser(userByCode[a.code])} data-testid={`btn-edit-user-${a.code}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          {userByCode[a.code].id !== me?.id && (
                            <Button size="icon" variant="ghost" className="text-amber-400" title="Hapus akses login" onClick={() => openDeleteUser(userByCode[a.code])} data-testid={`btn-delete-user-${a.code}`}>
                              <UserX className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                      <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => { setDeleteTarget(a); setDeletePin(""); }} data-testid={`btn-delete-agent-${a.code}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  ) : <span className="text-xs text-muted-foreground">-</span>}
                </TableCell>
              </TableRow>
            ))}
            {isSuperAdmin && orphanUsers.map((u) => (
              <TableRow key={`user-${u.id}`} data-testid={`user-row-${u.email}`}>
                <TableCell><span className="text-xs text-muted-foreground">-</span></TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-mono text-muted-foreground text-xs">{u.email}</TableCell>
                <TableCell>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono border ${ROLE_STYLE[u.role] || ROLE_STYLE.teknisi}`} data-testid={`user-role-${u.email}`}>
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" className="text-sky-400" title="Edit akses login (nama/role/PIN)" onClick={() => openEditUser(u)} data-testid={`btn-edit-user-${u.email}`}>
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  {u.id !== me?.id && (
                    <Button size="icon" variant="ghost" className="text-amber-400" title="Hapus akses login" onClick={() => openDeleteUser(u)} data-testid={`btn-delete-user-${u.email}`}>
                      <UserX className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </Card>

      {(isAdmin ? requests.some((r) => r.status === "pending") : requests.length > 0) && (
        <Card className="bg-card border-border shadow-sm" data-testid="reseller-requests-card">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              {isAdmin ? `Pengajuan Reseller Pending (${requests.filter((r) => r.status === "pending").length})` : "Status Pengajuan Reseller Saya"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(isAdmin ? requests.filter((r) => r.status === "pending") : requests.slice(0, 10)).map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-4 py-3" data-testid={`reseller-req-${r.id}`}>
                <div className="min-w-0 flex-1 basis-48">
                  <div className="font-medium text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {r.email || "-"} {r.phone ? `• ${r.phone}` : ""} • diajukan oleh {r.requested_by_name || r.requested_by}
                  </div>
                </div>
                {isAdmin ? (
                  <div className="flex gap-1.5">
                    <Button size="sm" onClick={() => openApprove(r)} data-testid={`btn-approve-reseller-${r.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Setujui
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-500" onClick={() => { setRejectReq(r); setRejectPin(""); }} data-testid={`btn-reject-reseller-${r.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Tolak
                    </Button>
                  </div>
                ) : (
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono border ${r.status === "approved" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : r.status === "rejected" ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-amber-500/10 text-amber-500 border-amber-500/20"}`} data-testid={`reseller-status-${r.id}`}>
                    {r.status === "approved" ? `Disetujui (${r.agent_code || ""})` : r.status === "rejected" ? "Ditolak" : "Menunggu persetujuan"}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Dialog: Tambah Agen */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-add-agent">
          <DialogHeader><DialogTitle className="text-base">{isAdmin ? "Tambah Agen / Member" : "Tambah Reseller"}</DialogTitle></DialogHeader>
          {!isAdmin && (
            <p className="text-[11px] text-muted-foreground">Pengajuan reseller akan ditinjau admin. Jika disetujui, reseller otomatis menjadi agen seri C dengan Anda sebagai upline.</p>
          )}
          <form onSubmit={submit} className="space-y-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5">
              {isAdmin && (
                <div className="space-y-1"><Label className="text-xs">Kode Agen (opsional — otomatis)</Label><Input className="h-8 text-xs" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder={`Otomatis: ${SERI_BY_TYPE[form.type]}001, ${SERI_BY_TYPE[form.type]}002, ...`} data-testid="input-agent-code" /></div>
              )}
              <div className="space-y-1"><Label className="text-xs">Nama</Label><Input className="h-8 text-xs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Konter Barokah" data-testid="input-agent-name" /></div>
              {isAdmin && (
                <div className="space-y-1"><Label className="text-xs">Tipe</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, hidden_menus: v === "Reseller Voucher" ? [...RESELLER_VOUCHER_HIDDEN] : [] })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-agent-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{AGENT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1"><Label className="text-xs">No. HP</Label><Input className="h-8 text-xs" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" data-testid="input-agent-phone" /></div>
              {isAdmin && (
                <div className="space-y-1"><Label className="text-xs">Upline / Agen Induk (opsional)</Label>
                  <AgentSearchSelect
                    options={[{ value: "-", label: "Tanpa upline", search: "tanpa upline" }, ...agents.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))]}
                    value={form.upline_code || "-"} onChange={(v) => setForm({ ...form, upline_code: v })} placeholder="Tanpa upline" testid="select-agent-upline" triggerClassName="h-8 text-xs" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Email {isAdmin ? "(opsional — jika diisi, otomatis bisa login)" : "Reseller (wajib)"}</Label>
                <Input className="h-8 text-xs" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required={!isAdmin} placeholder="reseller@gmail.com" data-testid="input-agent-email" />
                {!isAdmin && <p className="text-[10px] text-muted-foreground">Jika disetujui super admin, email ini bisa didaftarkan agar reseller bisa login ke sistem.</p>}
              </div>
              {isAdmin && form.email.trim() && (
                <>
                  <div className="space-y-1"><Label className="text-xs">PIN Pengguna (4-6 digit, untuk akses halaman)</Label><Input className="h-8 text-xs" type="password" inputMode="numeric" value={form.user_pin} onChange={(e) => setForm({ ...form, user_pin: e.target.value })} required minLength={4} placeholder="Contoh: 4521" data-testid="input-agent-user-pin" /></div>
                  <div className="space-y-1"><Label className="text-xs">PIN Super Admin (wajib)</Label><Input className="h-8 text-xs" type="password" inputMode="numeric" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-agent-admin-pin" /></div>
                </>
              )}
            </div>
            {isAdmin && (
              <div className="space-y-1"><Label className="text-xs">Batasi Akses Menu (yang dicentang = disembunyikan)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                  {BATASI_MENU.map((m) => (
                    <label key={m.path} className="flex items-center gap-1.5 text-[11px] rounded-md border border-border px-1.5 py-1 cursor-pointer hover:bg-accent/40 transition-colors" data-testid={`chk-hide-${m.path.slice(1)}`}>
                      <input type="checkbox" className="accent-[#F04E23] w-3.5 h-3.5" checked={form.hidden_menus.includes(m.path)} onChange={() => setForm({ ...form, hidden_menus: form.hidden_menus.includes(m.path) ? form.hidden_menus.filter((p) => p !== m.path) : [...form.hidden_menus, m.path] })} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button type="submit" className="w-full h-9 text-xs" disabled={busy} data-testid="btn-save-agent">{busy ? "Memproses..." : isAdmin ? "Simpan Agen" : "Kirim Pengajuan"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Agen (PIN) */}
      <Dialog open={!!editAgent} onOpenChange={() => setEditAgent(null)}>
        <DialogContent data-testid="dialog-edit-agent">
          <DialogHeader><DialogTitle>Edit Agen: {editAgent?.code}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="space-y-1.5"><Label>Nama</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="input-edit-agent-name" /></div>
            <div className="space-y-1.5"><Label>Tipe</Label>
              <Select value={editForm.type} onValueChange={(v) => setEditForm({ ...editForm, type: v })}>
                <SelectTrigger data-testid="select-edit-agent-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(AGENT_TYPES.includes(editForm.type) ? AGENT_TYPES : [editForm.type, ...AGENT_TYPES]).map((t) => <SelectItem key={t} value={t}>{t}{AGENT_TYPES.includes(t) ? "" : " (data lama)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Upline / Jaringan <span className="text-muted-foreground font-normal">(kosongkan = lepas dari upline)</span></Label>
              <Input list="upline-codes" value={editForm.upline_code ?? ""} onChange={(e) => setEditForm({ ...editForm, upline_code: e.target.value })} placeholder="Ketik kode / nama upline, mis. A006" className="font-mono" data-testid="input-edit-agent-upline" />
              <datalist id="upline-codes">
                {agents.filter((x) => x.code !== editAgent?.code).map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
              </datalist>
              {editForm.upline_code && editForm.upline_code.split(" ")[0].toUpperCase() !== (editAgent?.upline_code || "") && (
                <p className="text-[11px] text-amber-500" data-testid="upline-change-note">Jaringan akan dipindah dari {editAgent?.upline_code || "-"} ke {editForm.upline_code.split(" ")[0].toUpperCase()}. Aturan komisi untuk upline lama otomatis dialihkan ke upline baru.</p>
              )}
            </div>
            <div className="space-y-1.5"><Label>Batasi Akses Menu (yang dicentang = disembunyikan)</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {BATASI_MENU.map((m) => (
                  <label key={m.path} className="flex items-center gap-2 text-xs rounded-lg border border-border px-2.5 py-2 cursor-pointer hover:bg-accent/40 transition-colors" data-testid={`chk-edit-hide-${m.path.slice(1)}`}>
                    <input type="checkbox" className="accent-[#F04E23]" checked={(editForm.hidden_menus || []).includes(m.path)} onChange={() => setEditForm({ ...editForm, hidden_menus: (editForm.hidden_menus || []).includes(m.path) ? editForm.hidden_menus.filter((p) => p !== m.path) : [...(editForm.hidden_menus || []), m.path] })} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs rounded-lg border border-sky-500/40 bg-sky-500/5 px-2.5 py-2 cursor-pointer" data-testid="chk-edit-can-open-branch">
              <input type="checkbox" className="accent-sky-500" checked={!!editForm.can_open_branch} onChange={(e) => setEditForm({ ...editForm, can_open_branch: e.target.checked })} />
              <span><b>Izinkan Buka Cabang Baru</b> — menu "Buka Cabang Baru" tampil di beranda agen ini</span>
            </label>
            <div className="space-y-1.5"><Label>No. HP</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="input-edit-agent-phone" /></div>
            <div className="space-y-1.5">
              <Label>PIN Admin (wajib)</Label>
              <Input type="password" inputMode="numeric" value={editForm.pin} onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-edit-agent-pin" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="btn-update-agent">{busy ? "Memproses..." : "Simpan Perubahan"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Hapus Agen (PIN) */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent data-testid="dialog-delete-agent">
          <DialogHeader><DialogTitle>Hapus Agen {deleteTarget?.code}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Agen <span className="font-medium text-foreground">{deleteTarget?.name}</span> akan dihapus permanen. Riwayat komisi pada transaksi lama tetap tersimpan.</p>
          <form onSubmit={confirmDelete} className="space-y-3">
            <div className="space-y-1.5">
              <Label>PIN Admin (wajib untuk menghapus)</Label>
              <Input type="password" inputMode="numeric" value={deletePin} onChange={(e) => setDeletePin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-delete-agent-pin" />
            </div>
            <Button type="submit" variant="destructive" className="w-full" disabled={busy} data-testid="btn-confirm-delete-agent">{busy ? "Memproses..." : "Ya, Hapus Agen"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Jaringan Komisi */}
      <Dialog open={!!rulesAgent} onOpenChange={() => setRulesAgent(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="dialog-agent-rules">
          <DialogHeader><DialogTitle>Jaringan Komisi: {rulesAgent?.code} — {rulesAgent?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Setiap transaksi yang dicatat atas nama agen ini, komisi otomatis mengalir ke agen penerima berikut.
            Bebas lintas jaringan — bisa ke upline, downline, atau agen manapun.
          </p>
          <div className="rounded-xl border border-border bg-accent/30 p-3" data-testid="downline-section">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                Downline terdaftar ({downlines.length})
              </div>
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="h-7 pl-8 text-xs" placeholder="Cari kode / nama downline..." value={downlineSearch} onChange={(e) => setDownlineSearch(e.target.value)} data-testid="input-downline-search" />
              </div>
            </div>
            {downlines.length ? (
              (() => {
                const q = downlineSearch.trim().toLowerCase();
                const filtered = q ? downlines.filter((d) => `${d.code} ${d.name}`.toLowerCase().includes(q)) : downlines;
                return (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {filtered.map((d) => (
                      <div key={d.code} className="flex items-center gap-2 text-sm" data-testid={`downline-row-${d.code}`}>
                        <span className="px-2 py-0.5 rounded text-xs font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{d.code}</span>
                        <span className="font-medium">{d.name}</span>
                        <span className={`ml-auto px-2 py-0.5 rounded-full text-[11px] font-mono border ${TYPE_STYLE[d.type] || TYPE_STYLE["Teknisi"]}`}>{d.type || "Teknisi"}</span>
                      </div>
                    ))}
                    {!filtered.length && <p className="text-xs text-muted-foreground" data-testid="downline-search-empty">Tidak ada downline yang cocok dengan "{downlineSearch}"</p>}
                  </div>
                );
              })()
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="downline-empty">Belum ada downline. Downline otomatis muncul saat Anda mendaftarkan email pengguna dengan upline agen ini.</p>
            )}
          </div>
          <div className="space-y-2">
            {rules.map((r, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`rule-row-${i}`}>
                <AgentSearchSelect
                  options={agents.filter((a) => a.code !== rulesAgent?.code).map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))}
                  value={r.agent_code}
                  onChange={(v) => setRules(rules.map((x, idx) => idx === i ? { ...x, agent_code: v } : x))}
                  placeholder="Agen penerima" testid={`rule-select-agent-${i}`} triggerClassName="flex-1" />
                <Select value={r.type || "flat"} onValueChange={(v) => setRules(rules.map((x, idx) => idx === i ? { ...x, type: v } : x))}>
                  <SelectTrigger className="w-28" data-testid={`rule-select-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Rp Tetap</SelectItem>
                    <SelectItem value="percent">% Omset</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" min="0" className="w-28" placeholder="Nilai" value={r.value ?? ""}
                  onChange={(e) => setRules(rules.map((x, idx) => idx === i ? { ...x, value: Number(e.target.value) } : x))}
                  data-testid={`rule-input-value-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="text-rose-400" onClick={() => setRules(rules.filter((_, idx) => idx !== i))} data-testid={`rule-btn-remove-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setRules([...rules, { agent_code: "", type: "flat", value: 0 }])} data-testid="btn-add-rule">
              <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Penerima Komisi
            </Button>
          </div>
          <Button className="w-full" onClick={saveRules} disabled={savingRules} data-testid="btn-save-rules">
            {savingRules ? "Menyimpan..." : "Simpan Aturan Jaringan"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="dialog-import-excel">
          <DialogHeader><DialogTitle>Impor Agen dari Excel</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Unggah file Excel jaringan agen (4 baris judul, lalu kolom: No | Kode Agen | Email | Nama | Tipe | Upline | Batasi Akses Menu). Teknisi Kepala di baris pertama data, downline di baris berikutnya. Agen yang sudah terdaftar otomatis dilewati.</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>File Excel (.xlsx)</Label>
              <Input type="file" accept=".xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="input-import-file" />
            </div>
            <div className="space-y-1.5"><Label>PIN Default Akun Baru</Label>
              <Input value={importPin} onChange={(e) => setImportPin(e.target.value)} className="font-mono" data-testid="input-import-pin" />
              <p className="text-xs text-muted-foreground">Dipakai untuk semua akun baru — sampaikan ke mitra agar segera mengganti PIN.</p>
            </div>
          </div>
          {importResult && (
            <div className="rounded-xl border border-border bg-accent/30 p-3 text-xs font-mono space-y-1" data-testid="import-result">
              <div className="text-emerald-400 font-semibold">{importResult.created} agen berhasil didaftarkan</div>
              {importResult.skipped.length > 0 && <div className="text-amber-400">{importResult.skipped.length} dilewati: {importResult.skipped.join(", ")}</div>}
            </div>
          )}
          <Button className="w-full" onClick={submitImport} disabled={importBusy || !importFile} data-testid="btn-import-submit">
            {importBusy ? "Mengimpor..." : "Mulai Impor"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-sm" data-testid="dialog-transfer-saldo">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">{transferToUpline ? "Transfer Saldo ke Upline" : "Transfer Saldo ke Downline"}</DialogTitle>
            <DialogDescription className="text-xs">{transferToUpline ? "Hanya bisa ke upline Anda — tidak bisa ke sesama agen." : "Hanya bisa ke jaringan downline Anda sendiri — tidak bisa antar agen lain."} Tercatat di mutasi saldo kedua pihak.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-3">
            <div className="space-y-1.5"><Label>{transferToUpline ? "Upline Penerima" : "Downline Penerima"}</Label>
              <AgentSearchSelect
                options={mySubtree.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))}
                value={transferForm.to_code} onChange={(v) => setTransferForm({ ...transferForm, to_code: v })}
                placeholder={transferToUpline ? "Pilih upline" : "Pilih downline"} testid="select-transfer-to" />
            </div>
            <div className="space-y-1.5"><Label>Nominal (Rp)</Label><Input type="number" min="1" value={transferForm.amount} onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} required placeholder="50000" data-testid="input-transfer-amount" /></div>
            <div className="space-y-1.5"><Label>PIN Anda (wajib)</Label><Input type="password" inputMode="numeric" value={transferForm.pin} onChange={(e) => setTransferForm({ ...transferForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-transfer-pin" /></div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={transferBusy || !transferForm.to_code} data-testid="btn-transfer-confirm">{transferBusy ? "Memproses..." : "Transfer Sekarang"}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <InactiveAgentsDialog open={inactiveOpen} onClose={() => setInactiveOpen(false)} onDeleted={load} />
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="dialog-bulk-commission">
          <DialogHeader><DialogTitle>Komisi Upline Massal</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Terapkan aturan komisi ke upline untuk seluruh downline sekaligus. Setiap transaksi downline, komisi otomatis mengalir ke Teknisi Kepala. Aturan lintas agen lain tetap bisa diatur per agen lewat tombol "Jaringan" di tabel.</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Teknisi Kepala (Upline)</Label>
              <Select value={bulkForm.upline_code} onValueChange={(v) => {
                const dl = agents.find((a) => a.upline_code === v && (a.commission_rules || []).some((r) => r.agent_code === v));
                const cur = dl ? (dl.commission_rules || []).find((r) => r.agent_code === v) : null;
                setBulkForm({ ...bulkForm, upline_code: v, type: cur ? cur.type : "percent", value: cur ? cur.value : 0 });
              }}>
                <SelectTrigger data-testid="select-bulk-upline"><SelectValue placeholder="Pilih upline" /></SelectTrigger>
                <SelectContent>
                  {agents.filter((a) => a.type === "Teknisi Kepala").map((a) => (
                    <SelectItem key={a.code} value={a.code} data-testid={`bulk-upline-${a.code}`}>{a.code} — {a.name} ({downlineCount[a.code] || 0} downline)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jenis</Label>
                <Select value={bulkForm.type} onValueChange={(v) => setBulkForm({ ...bulkForm, type: v })}>
                  <SelectTrigger data-testid="select-bulk-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="flat">Rp Tetap / transaksi</SelectItem><SelectItem value="percent">% dari Omset</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Nilai</Label>
                <Input type="number" min="0" value={bulkForm.value} onChange={(e) => setBulkForm({ ...bulkForm, value: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="input-bulk-value" />
              </div>
            </div>
            {bulkForm.upline_code && (
              <div className="space-y-1" data-testid="bulk-preview">
                <p className="text-xs font-mono text-muted-foreground">Akan diterapkan ke {downlineCount[bulkForm.upline_code] || 0} downline {bulkForm.upline_code}.</p>
                <p className={`text-[11px] font-mono ${bulkCurrentRule ? "text-emerald-400" : "text-amber-400"}`} data-testid="bulk-current">
                  Tersimpan saat ini: {bulkCurrentRule ? `${bulkCurrentRule.value}${bulkCurrentRule.type === "percent" ? "% dari omset" : " flat per transaksi"}` : "tidak ada aturan (komisi 0)"}
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Nilai 0 menghapus aturan komisi upline dari semua downline.</p>
          </div>
          <Button className="w-full" onClick={saveBulkRules} disabled={bulkBusy || !bulkForm.upline_code} data-testid="btn-bulk-apply">
            {bulkBusy ? "Menerapkan..." : "Terapkan ke Semua Downline"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Pengguna (PIN) */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent data-testid="dialog-edit-user">
          <DialogHeader><DialogTitle>Edit Pengguna: {editUser?.email}</DialogTitle></DialogHeader>
          <form onSubmit={submitEditUser} className="space-y-3">
            <div className="space-y-1.5"><Label>Email Login (Gmail)</Label><Input type="email" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} required data-testid="input-edit-user-email" />
              <p className="text-[10px] text-muted-foreground">Mengganti email = agen selanjutnya login Google memakai email baru ini.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nama</Label><Input value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} required data-testid="input-edit-user-name" /></div>
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={editUserForm.role} onValueChange={(v) => setEditUserForm({ ...editUserForm, role: v })}>
                  <SelectTrigger data-testid="select-edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Kode Agen</Label>
                <AgentSearchSelect
                  options={[{ value: "-", label: "Tidak terikat agen", search: "tidak terikat" }, ...agents.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))]}
                  value={editUserForm.agent_code || "-"} onChange={(v) => setEditUserForm({ ...editUserForm, agent_code: v })} placeholder="Tidak terikat agen" testid="select-edit-user-agent" />
              </div>
              <div className="space-y-1.5"><Label>PIN Baru (kosongkan jika tetap)</Label><Input type="password" inputMode="numeric" value={editUserForm.user_pin} onChange={(e) => setEditUserForm({ ...editUserForm, user_pin: e.target.value })} data-testid="input-edit-user-new-pin" /></div>
            </div>
            <div className="space-y-1.5"><Label>PIN Super Admin (wajib)</Label><Input type="password" inputMode="numeric" value={editUserForm.pin} onChange={(e) => setEditUserForm({ ...editUserForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-edit-user-pin" /></div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="btn-update-user">{busy ? "Memproses..." : "Simpan Perubahan"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Hapus Pengguna (PIN) */}
      <Dialog open={!!deleteUserTarget} onOpenChange={() => setDeleteUserTarget(null)}>
        <DialogContent data-testid="dialog-delete-user">
          <DialogHeader><DialogTitle>Hapus Pengguna {deleteUserTarget?.email}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Akses login <span className="font-medium text-foreground">{deleteUserTarget?.name}</span> ({ROLE_LABEL[deleteUserTarget?.role] || deleteUserTarget?.role}) akan dicabut permanen. Entri agennya tetap tersimpan untuk riwayat komisi.</p>
          <form onSubmit={submitDeleteUser} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> PIN Super Admin (wajib untuk menghapus)</Label>
              <Input type="password" inputMode="numeric" value={deleteUserPin} onChange={(e) => setDeleteUserPin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-delete-user-pin" />
            </div>
            <Button type="submit" variant="destructive" className="w-full" disabled={busy} data-testid="btn-confirm-delete-user">{busy ? "Memproses..." : "Ya, Hapus Pengguna"}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!approveReq} onOpenChange={() => setApproveReq(null)}>
        <DialogContent data-testid="dialog-approve-reseller">
          <DialogHeader><DialogTitle>Setujui Pengajuan Reseller</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Periksa/koreksi data sebelum menyetujui. Reseller akan dibuat sebagai agen seri C dengan upline {approveReq?.requested_by}.</p>
          <form onSubmit={submitApprove} className="space-y-3">
            <div className="space-y-1.5"><Label>Nama</Label><Input value={approveForm.name} onChange={(e) => setApproveForm({ ...approveForm, name: e.target.value })} required data-testid="input-approve-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={approveForm.email} onChange={(e) => setApproveForm({ ...approveForm, email: e.target.value })} required data-testid="input-approve-email" /></div>
            <div className="space-y-1.5"><Label>No. HP</Label><Input value={approveForm.phone} onChange={(e) => setApproveForm({ ...approveForm, phone: e.target.value })} data-testid="input-approve-phone" /></div>
            <div className="space-y-1.5">
              <Label>PIN Admin (wajib)</Label>
              <Input type="password" inputMode="numeric" value={approveForm.pin} onChange={(e) => setApproveForm({ ...approveForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-approve-pin" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="btn-confirm-approve">{busy ? "Memproses..." : "Setujui & Buat Reseller"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectReq} onOpenChange={() => setRejectReq(null)}>
        <DialogContent data-testid="dialog-reject-reseller">
          <DialogHeader><DialogTitle>Tolak Pengajuan Reseller?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Pengajuan <span className="font-medium text-foreground">{rejectReq?.name}</span> ({rejectReq?.email}) akan ditolak dan tidak dibuat sebagai agen.</p>
          <form onSubmit={submitReject} className="space-y-3">
            <div className="space-y-1.5">
              <Label>PIN Admin (wajib)</Label>
              <Input type="password" inputMode="numeric" value={rejectPin} onChange={(e) => setRejectPin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-reject-pin" />
            </div>
            <Button type="submit" variant="destructive" className="w-full" disabled={busy} data-testid="btn-confirm-reject">{busy ? "Memproses..." : "Ya, Tolak Pengajuan"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustTarget} onOpenChange={() => setAdjustTarget(null)}>
        <DialogContent data-testid="dialog-saldo-adjust">
          <DialogHeader><DialogTitle>{adjustTarget?.mode === "sub" ? "Kurangi" : "Tambah"} Saldo Manual: {adjustTarget?.agent.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-3">
            <p className="text-xs text-muted-foreground">Akun: <span className="font-mono">{adjustTarget?.user?.email}</span> • Saldo saat ini: <span className="font-mono font-semibold text-foreground" data-testid="adjust-current-balance">{rupiah(adjustTarget?.user?.balance || 0)}</span></p>
            <div className="space-y-1.5"><Label>Nominal (Rp)</Label><Input type="number" min="1" value={adjustForm.amount} onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} required placeholder="50000" data-testid="input-adjust-amount" /></div>
            {adjustTarget?.mode === "sub" && Number(adjustForm.amount) > Number(adjustTarget?.user?.balance || 0) && <p className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-500 px-2 py-1.5" data-testid="adjust-minus-warning">Saldo akan menjadi <b>MINUS {rupiah(Number(adjustForm.amount) - Number(adjustTarget?.user?.balance || 0))}</b>. Agen akan diberi tahu lewat chat dan wajib setor untuk melunasi.</p>}
            <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Input value={adjustForm.note} onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })} data-testid="input-adjust-note" /></div>
            <div className="space-y-1.5"><Label>PIN Admin (wajib)</Label><Input type="password" inputMode="numeric" value={adjustForm.pin} onChange={(e) => setAdjustForm({ ...adjustForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-adjust-pin" /></div>
            <Button type="submit" variant={adjustTarget?.mode === "sub" ? "destructive" : "default"} className="w-full" disabled={busy} data-testid="btn-confirm-adjust">
              {busy ? "Memproses..." : adjustTarget?.mode === "sub" ? "Kurangi Saldo" : "Tambah Saldo"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mutasiTarget} onOpenChange={() => setMutasiTarget(null)}>
        <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="dialog-mutasi">
          <DialogHeader className="sr-only"><DialogTitle>Saldo & Mutasi {mutasiTarget?.agent.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
            {/* Panel kiri: Tambah Saldo */}
            <div className="border-b md:border-b-0 md:border-r border-border">
              <div className="px-4 py-3 bg-[#F04E23] text-white font-heading font-bold text-sm" data-testid="topup-header">Tambah Saldo</div>
              <form onSubmit={submitTopup} className="p-4 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Tipe Pembayaran</Label>
                  <Select value={topupForm.pay_type} onValueChange={(v) => setTopupForm({ ...topupForm, pay_type: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-pay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Tunai", "Transfer Bank", "QRIS", "Hutang Saldo"].map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nominal Saldo</Label>
                  <Input type="number" min="1" className="h-8 text-xs font-mono" value={topupForm.amount} onChange={(e) => setTopupForm({ ...topupForm, amount: e.target.value })} required placeholder="50000" data-testid="input-topup-amount" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Keterangan</Label>
                  <Input className="h-8 text-xs" value={topupForm.note} onChange={(e) => setTopupForm({ ...topupForm, note: e.target.value })} placeholder="isi saldo" data-testid="input-topup-note" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">PIN Admin (wajib)</Label>
                  <Input type="password" inputMode="numeric" className="h-8 text-xs" value={topupForm.pin} onChange={(e) => setTopupForm({ ...topupForm, pin: e.target.value })} required placeholder="Masukkan PIN" data-testid="input-topup-pin" />
                </div>
                <Button type="submit" className="w-full h-9 bg-[#F04E23] hover:bg-[#d94420] text-white text-xs font-bold" disabled={busy} data-testid="btn-topup-proses">
                  {busy ? "Memproses..." : "Proses"}
                </Button>
              </form>
            </div>
            {/* Panel kanan: Informasi Agen + Mutasi */}
            <div className="min-w-0">
              <div className="px-4 py-3 bg-sky-500/15 border-b border-border font-heading font-bold text-sm text-sky-400" data-testid="agent-info-header">Informasi Agen</div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start" data-testid="mutasi-agent-info">
                  <div className="rounded-xl border border-border overflow-hidden text-xs">
                    <div className="px-3 py-2 bg-accent/40 font-semibold flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-sky-400" /> Data Agen</div>
                    <div className="divide-y divide-border">
                      <div className="grid grid-cols-[110px_1fr] px-3 py-1.5"><span className="text-muted-foreground">Kode Agen</span><span className="font-mono font-bold text-[#F04E23]" data-testid="mutasi-agent-code">{mutasiTarget?.agent.code}</span></div>
                      <div className="grid grid-cols-[110px_1fr] px-3 py-1.5"><span className="text-muted-foreground">Email</span><span className="font-mono break-all" data-testid="mutasi-agent-email">{mutasiTarget?.user?.email || "-"}</span></div>
                      <div className="grid grid-cols-[110px_1fr] px-3 py-1.5"><span className="text-muted-foreground">Nama</span><span className="font-medium" data-testid="mutasi-agent-name">{mutasiTarget?.agent.name}</span></div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 min-w-44">
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Saldo Sekarang :</div>
                      <div className={`font-mono font-bold text-2xl ${(mutasiTarget?.user?.balance || 0) < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid="mutasi-saldo-sekarang">{rupiah(mutasiTarget?.user?.balance || 0).replace("Rp", "").trim()}</div>
                    </div>
                    {mutasiTarget?.user?.id && <PinjamSaldoBanner key={mutasiTarget.debtKey || 0} userId={mutasiTarget.user.id} canEdit compact testid="mutasi-hutang" />}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs font-semibold flex items-center gap-1.5"><LineChart className="w-3.5 h-3.5 text-sky-400" /> Mutasi Saldo Agen</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex rounded-lg border border-border overflow-hidden text-[10px] font-mono" data-testid="mutasi-filter">
                        {[["all", "Semua", ""], ["masuk", "Masuk", "text-sky-400"], ["keluar", "Keluar", "text-rose-400"], ["komisi", "Komisi", "text-emerald-400"]].map(([k, lbl, cls]) => (
                          <button key={k} type="button" onClick={() => setMutasiFilter(k)} className={`px-2.5 py-1 transition-colors ${mutasiFilter === k ? `bg-accent font-bold ${cls}` : "text-muted-foreground hover:bg-accent/50"}`} data-testid={`mutasi-filter-${k}`}>{lbl}</button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <DatePicker className="h-9 w-40 text-xs cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:p-1 [&::-webkit-calendar-picker-indicator]:opacity-80" value={mutasiDate} onChange={(v) => loadMutasi(mutasiTarget.user, v, mutasiTo)} placeholder="Dari tanggal (kosongkan = terakhir)" testid="input-mutasi-date" />
                        <span className="text-[10px] text-muted-foreground">s/d</span>
                        <DatePicker className="h-9 w-40 text-xs cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:p-1 [&::-webkit-calendar-picker-indicator]:opacity-80" value={mutasiTo} min={mutasiDate } onChange={(v) => loadMutasi(mutasiTarget.user, mutasiDate, v)} placeholder="Sampai tanggal (kosongkan = 1 hari)" testid="input-mutasi-date-to" />
                      </div>
                    </div>
                  </div>
                  {mutasiLogs.length > 0 && (
                    <div className="flex gap-4 justify-end flex-wrap text-[11px] font-mono" data-testid="mutasi-summary">
                      {(mutasiDate || mutasiTo) && <span className="text-muted-foreground" data-testid="mutasi-period">Periode: {fmtDate(mutasiDate || mutasiTo)}{mutasiTo && mutasiDate && mutasiTo !== mutasiDate ? ` – ${fmtDate(mutasiTo)}` : ""}</span>}
                      <span className="text-emerald-400" data-testid="mutasi-total-in">Masuk: +{rupiah(mutasiIn)}</span>
                      <span className="text-rose-400" data-testid="mutasi-total-out">Keluar: {rupiah(mutasiOut)}</span>
                      <span className="text-emerald-300" data-testid="mutasi-total-komisi">Komisi: +{rupiah(mutasiKomisi)}</span>
                      <span className="text-sky-400" data-testid="mutasi-total-count">{mutasiLogs.length} transaksi</span>
                    </div>
                  )}
                  <div className="rounded-xl border border-border overflow-x-auto">
                    <Table className="min-w-[640px]" data-testid="mutasi-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Tanggal</TableHead>
                          <TableHead className="text-xs">Tipe</TableHead>
                          <TableHead className="text-xs">Deskripsi</TableHead>
                          <TableHead className="text-xs text-right">Keluar</TableHead>
                          <TableHead className="text-xs text-right">Masuk</TableHead>
                          <TableHead className="text-xs text-right">Saldo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          let bal = mutasiTarget?.user?.balance || 0;
                          const rows = mutasiLogs.map((l) => {
                            const rowBal = bal;
                            bal -= l.amount;
                            const isKomisi = /komisi|insentive|incentive/i.test(l.note || "");
                            const kind = isKomisi ? "komisi" : l.amount >= 0 ? "masuk" : "keluar";
                            return { l, rowBal, isKomisi, kind };
                          }).filter((r) => mutasiFilter === "all" || r.kind === mutasiFilter);
                          if (!rows.length && mutasiLogs.length) return <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground" data-testid="mutasi-filter-empty">Tidak ada mutasi {mutasiFilter} pada daftar ini</TableCell></TableRow>;
                          return rows.map(({ l, rowBal, isKomisi }) => {
                            const tipe = isKomisi ? "Komisi" : l.amount >= 0 ? "MASUK" : "KELUAR";
                            const tipeCls = isKomisi ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : l.amount >= 0 ? "bg-sky-500/10 text-sky-400 border-sky-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20";
                            return (
                              <TableRow key={l.id} data-testid={`mutasi-${l.id}`}>
                                <TableCell className="text-[11px] font-mono whitespace-nowrap text-muted-foreground">{fmtDateTime(l.created_at)}</TableCell>
                                <TableCell><span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${tipeCls}`}>{tipe}</span></TableCell>
                                <TableCell className="text-[11px] break-words max-w-64">
                                  {isSuperAdmin && l.voucher_link ? <a href={l.voucher_link} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline underline-offset-2" title="Buka batch voucher ini" data-testid={`mutasi-voucher-link-${l.id}`}>{l.note} ↗</a> : l.note}
                                  <span className="block text-[10px] text-muted-foreground font-mono">oleh {l.by_label || l.by || "sistem"}{l.batch_id && <span className="ml-1 text-sky-500/70">• {l.batch_id}</span>}</span>
                                </TableCell>
                                <TableCell className="text-right font-mono text-[11px] text-rose-400">{l.amount < 0 ? rupiah(-l.amount).replace("Rp", "").trim() : "0"}</TableCell>
                                <TableCell className="text-right font-mono text-[11px] text-emerald-400">{l.amount >= 0 ? rupiah(l.amount).replace("Rp", "").trim() : "0"}</TableCell>
                                <TableCell className="text-right font-mono text-[11px] font-semibold">{rupiah(rowBal).replace("Rp", "").trim()}</TableCell>
                              </TableRow>
                            );
                          });
                        })()}
                        {!mutasiLogs.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground" data-testid="mutasi-empty">Tidak ada mutasi{mutasiDate || mutasiTo ? " pada periode ini" : ""}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!routersAgent} onOpenChange={() => setRoutersAgent(null)}>
        <DialogContent data-testid="dialog-agent-routers">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-sky-400" /> Cabang Tertaut: {routersAgent?.agent.code} — {routersAgent?.agent.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Centang cabang yang boleh diakses <span className="font-mono text-foreground">{routersAgent?.user?.email}</span>. Hapus centang untuk melepas tautan — tidak perlu masuk ke tiap cabang lagi.</p>
          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs" data-testid="linked-routers-summary">
            <span className="font-semibold text-sky-400">Cabang tertaut ({routersChecked.length}):</span>{" "}
            {routersChecked.length
              ? routersList.filter((r) => routersChecked.includes(r.id)).map((r) => r.name).sort((a, b) => a.localeCompare(b)).join(", ")
              : <span className="text-muted-foreground">belum ada cabang yang ditautkan</span>}
          </div>
          <form onSubmit={saveRouters} className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Cari nama cabang / host..." value={routersSearch} onChange={(e) => setRoutersSearch(e.target.value)} className="pl-9" data-testid="input-search-router" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto" data-testid="routers-checklist">
              {routersList.filter((r) => !routersSearch || `${r.name} ${r.host || ""}`.toLowerCase().includes(routersSearch.toLowerCase()))
                .sort((a, b) => (routersChecked.includes(b.id) - routersChecked.includes(a.id)) || a.name.localeCompare(b.name))
                .map((r) => {
                  const on = routersChecked.includes(r.id);
                  return (
                    <label key={r.id} className={`flex items-center gap-2 text-xs rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${on ? "border-sky-500/50 bg-sky-500/10" : "border-border hover:bg-accent/40"}`} data-testid={`chk-router-${r.name}`}>
                      <input type="checkbox" className="accent-[#F04E23]" checked={on} onChange={() => setRoutersChecked(on ? routersChecked.filter((x) => x !== r.id) : [...routersChecked, r.id])} />
                      <span className="min-w-0 truncate">{r.name}{r.host ? <span className="text-muted-foreground"> — {r.host}</span> : null}</span>
                    </label>
                  );
                })}
              {!routersList.length && <div data-testid="routers-loading"><FibiLoading text="FIBI sedang memuat daftar cabang..." size={20} /></div>}
              {routersList.length > 0 && !routersList.filter((r) => !routersSearch || `${r.name} ${r.host || ""}`.toLowerCase().includes(routersSearch.toLowerCase())).length && <p className="text-xs text-muted-foreground col-span-2 py-4 text-center" data-testid="routers-no-match">Tidak ada cabang yang cocok dengan "{routersSearch}"</p>}
            </div>
            <div className="space-y-1.5"><Label>PIN Super Admin (wajib)</Label><Input type="password" inputMode="numeric" value={routersPin} onChange={(e) => setRoutersPin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-routers-pin" /></div>
            <Button type="submit" className="w-full" disabled={busy || !routersList.length} data-testid="btn-save-routers">{busy ? "Memproses..." : "Simpan Tautan Cabang"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!chatTarget} onOpenChange={(o) => !o && setChatTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-agent-chat">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-400" /> Chat — {chatTarget?.name}</DialogTitle></DialogHeader>
          {chatTarget && <ChatPanel channel={chatTarget.channel} testid="agent-chat" templates={chatTarget.guide ? GUIDE_TEMPLATES : null} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
